import * as React from "react"
import { Card, ThemeCss, Theme, Button } from "gatsby-interface"
import { StructuredLogsTabs } from "@modules/build/logs/structured/components/StructuredLogsTabs"
import { BuildEventText } from "@modules/build/shared/components/BuildEventText"
import {
  BuildType,
  BuildRunnerType,
  BuildCommit,
  BuildStatus,
  BuildSource,
  CdnVendor,
  CdnIntegration,
  User,
  BuildRouteMetadata,
} from "@modules/graphql/types"
import BuildViewLink from "@modules/build/shared/components/BuildViewLink"
import BuildPreviewLink from "./PreviewLink"
import BuildStatusIndicator from "@modules/build/shared/components/BuildStatusIndicator"
import BuildCardStatusInfo from "@modules/build/card/components/BuildCardStatusInfo"
import { DeploymentStatus } from "@modules/build/card/components/DeploymentStatus"
import { DeploymentTiming } from "@modules/build/card/components/DeploymentTiming"
import BuildAuthor from "@modules/build/card/components/BuildAuthor"
import BuildCommitInfo from "@modules/build/shared/components/BuildCommitInfo"
import IncrementalBuildChip from "@modules/build/shared/components/IncrementalBuildChip"
import DateTime from "@modules/ui/components/DateTime"
import { GatsbyCloudLogo } from "@modules/brand/assets/GatsbyCloudLogo"
import { LighthouseScores } from "@modules/metrics/components/LighthouseScores"
import { PublishBuildConfirmation } from "@modules/build/shared/components/PublishBuildConfirmation"
import { useSiteDetailsQuery } from "@modules/site/shared/queries.generated"
import { CdnIntegrationChip } from "@modules/cdn/shared/components/CdnIntegrationChip"
import { build as text } from "@modules/locales/default.js"
import {
  rowCss,
  itemCss,
  actionsCss,
  separatorCss,
} from "@modules/build/card/stylesheets"
import { isProperlyConnected } from "@modules/site/cdnIntegrations"
import { useFlags } from "@modules/featureFlags"
import { BuildPageTypesStats } from "./BuildPageTypesStats"

export type NewBuildDetailsInfoProps = {
  buildId: string
  siteId: string
  organizationId: string
  runnerType: BuildRunnerType
  buildStatus: BuildStatus
  showStructuredLogs: boolean
  commit: BuildCommit
  buildType: BuildType
  source: BuildSource
  isBuildDetails: boolean
  previewUrl: string
  branch: string
  createdAt: string
  startedAt: string
  endedAt: string
  createdAtDate: string
  duration: number
  showDeploymentStatus: boolean
  deployStartedAt: string
  deployEndedAt: string
  mainIntegration: CdnIntegration
  author: Partial<User>
  repositoryUrl: string
  isIncrementalUpdate: boolean
  latestHostingDeployVersion: string
  showCdnIntegrations: boolean
  cdnVendor: CdnVendor
  isEligiblePlan: boolean
  gatsbyHostingOn: boolean
  routeMetadata?: BuildRouteMetadata
}

export function NewBuildDetailsInfo({
  buildId,
  siteId,
  organizationId,
  runnerType,
  buildStatus,
  showStructuredLogs,
  commit,
  buildType,
  source,
  isBuildDetails,
  previewUrl,
  branch,
  createdAt,
  startedAt,
  endedAt,
  createdAtDate,
  duration,
  showDeploymentStatus,
  deployStartedAt,
  deployEndedAt,
  mainIntegration,
  author,
  repositoryUrl,
  isIncrementalUpdate,
  showCdnIntegrations,
  cdnVendor,
  isEligiblePlan,
  routeMetadata,
}: NewBuildDetailsInfoProps) {
  const { flags } = useFlags()
  const { data: siteDetailsData } = useSiteDetailsQuery({
    variables: { id: siteId },
    fetchPolicy: "cache-and-network",
  })

  const cdnIntegrations = siteDetailsData?.siteDetails?.cdnIntegrations ?? []
  const activeIntegration = cdnIntegrations.find(isProperlyConnected)
  const gatsbyHostingOn = activeIntegration?.vendor === CdnVendor.CloudCdn

  const latestHostingDeployVersion =
    siteDetailsData?.siteDetails?.latestHostingDeployVersion
  const manualHostingDeploysEnabled =
    siteDetailsData?.siteDetails?.manualHostingDeploysEnabled

  const { dsgRouteCount, ssrRouteCount, ssgRouteCount } = routeMetadata || {}

  const isPublished = latestHostingDeployVersion === buildId

  const showDeployment =
    !gatsbyHostingOn || !isEligiblePlan
      ? true
      : isPublished ||
        (buildStatus === BuildStatus.Publishing &&
          !manualHostingDeploysEnabled) ||
        buildStatus === BuildStatus.PublishCanceled ||
        buildStatus === BuildStatus.PublishError
      ? true
      : false

  const [publishBuild, setPublishBuild] = React.useState<boolean>(false)
  const showPublishUI =
    isBuildDetails &&
    gatsbyHostingOn &&
    isEligiblePlan &&
    (buildStatus === BuildStatus.Success ||
      buildStatus === BuildStatus.PublishCanceled ||
      buildStatus === BuildStatus.PublishError)

  const isSucceffullyBuilt = [
    BuildStatus.Success,
    BuildStatus.Publishing,
    BuildStatus.PublishCanceled,
    BuildStatus.PublishError,
  ].includes(buildStatus)

  const onPublish = () => {
    setPublishBuild(true)
  }

  return (
    <React.Fragment>
      <Card css={cardCss}>
        <div css={headerCss}>
          <div>
            <BuildEventText
              commit={commit}
              buildType={buildType}
              buildSource={source}
              runnerType={runnerType}
              collapsible
            />
          </div>

          {isBuildDetails ? (
            <BuildViewLink
              buildId={buildId}
              buildStatus={buildStatus}
              uiSource="Build Details"
              organizationId={organizationId}
            />
          ) : (
            buildStatus === BuildStatus.Success && (
              <BuildPreviewLink previewUrl={previewUrl} />
            )
          )}
        </div>

        <div css={rowsCss}>
          <div css={rowCss}>
            <div css={itemCss}>
              <BuildStatusIndicator
                a11yId={buildId}
                buildStatus={buildStatus}
                runnerType={runnerType}
                branch={branch}
              />
              <BuildCardStatusInfo
                createdAt={createdAt}
                buildStatus={buildStatus}
                startedAt={startedAt}
                endedAt={endedAt}
                duration={duration}
              />
            </div>
            {showDeploymentStatus && showDeployment && (
              <React.Fragment>
                <span css={separatorCss}>›</span>
                <div css={itemCss}>
                  <DeploymentStatus value={buildStatus} />

                  <DeploymentTiming
                    status={buildStatus}
                    startDate={new Date(deployStartedAt)}
                    endDate={
                      deployEndedAt ? new Date(deployEndedAt) : undefined
                    }
                    vendor={mainIntegration?.vendor || ""}
                    showVendorName={false}
                  />
                </div>
              </React.Fragment>
            )}
            {showPublishUI && latestHostingDeployVersion === buildId && (
              <React.Fragment>
                <span css={separatorCss}>›</span>

                <div css={itemCss}>
                  <GatsbyCloudLogo
                    css={{
                      width: `1.5em`,
                      height: `auto`,
                    }}
                  />{" "}
                  <span
                    css={(theme: Theme) => ({
                      fontWeight: theme.fontWeights.bold,
                    })}
                  >
                    {text.labels.published}
                  </span>
                </div>
              </React.Fragment>
            )}

            {showCdnIntegrations && cdnVendor && (
              <CdnIntegrationChip
                vendor={cdnVendor}
                siteId={siteId}
                organizationId={organizationId}
              />
            )}

            {showPublishUI && latestHostingDeployVersion !== buildId && (
              <div css={actionsCss}>
                <Button
                  size="M"
                  variant="SECONDARY"
                  onClick={onPublish}
                  rightIcon={
                    <GatsbyCloudLogo
                      css={_ => ({
                        width: `1.5em`,
                        height: `auto`,
                      })}
                    />
                  }
                >
                  {text.actions.publishThisBuild}
                </Button>
              </div>
            )}
          </div>

          <div css={rowCss}>
            <BuildAuthor
              buildType={buildType}
              commit={commit}
              author={author ? author : undefined}
              source={source}
            />

            <DateTime date={createdAtDate} />

            <BuildCommitInfo
              branch={branch}
              commit={commit}
              repositoryUrl={repositoryUrl || ""}
              withLink={true}
              withIcon={true}
            />

            {isIncrementalUpdate && <IncrementalBuildChip />}
          </div>

          {flags.gatsbyv4 &&
            isSucceffullyBuilt &&
            (ssgRouteCount || dsgRouteCount || ssrRouteCount) && (
              <div css={rowCss}>
                <BuildPageTypesStats
                  ssgCount={ssgRouteCount}
                  dsgCount={dsgRouteCount}
                  ssrCount={ssrRouteCount}
                />
              </div>
            )}

          {isSucceffullyBuilt && (
            <div css={rowCss}>
              <LighthouseScores
                siteId={siteId}
                organizationId={organizationId}
                buildId={buildId}
                branch={branch}
              />
            </div>
          )}
        </div>

        {showStructuredLogs && (
          <StructuredLogsTabs
            buildId={buildId}
            siteId={siteId}
            organizationId={organizationId}
            runnerType={runnerType}
            buildStatus={buildStatus}
          />
        )}
      </Card>

      {!!publishBuild && (
        <PublishBuildConfirmation
          siteId={siteId}
          buildId={buildId}
          onDismiss={() => setPublishBuild(false)}
          onComplete={() => {
            setPublishBuild(false)
          }}
          manualHostingDeploysEnabled={manualHostingDeploysEnabled ?? undefined}
        />
      )}
    </React.Fragment>
  )
}

/* styles */

const cardCss: ThemeCss = theme => ({
  padding: `${theme.space[6]} ${theme.space[7]}`,

  [theme.mediaQueries.desktop]: {
    padding: `${theme.space[8]} ${theme.space[9]}`,
  },
})

const headerCss: ThemeCss = theme => ({
  display: `grid`,
  gap: theme.space[3],
})

const rowsCss: ThemeCss = theme => ({
  marginTop: theme.space[7],
  marginBottom: theme.space[7],
})
